import { Layer, Tile } from "ol/layer";
import { TileArcGISRest, TileWMS, XYZ } from "ol/source";
import { WebSourceAsset } from "../../../redux/assets-slice";
import { ServerType } from "../../../types/web-sources";
import { isEqual } from 'lodash';
import VectorSource from "ol/source/Vector";
import GeoJSON from 'ol/format/GeoJSON.js';
import { WebSourceList } from ".";
import {createXYZ} from 'ol/tilegrid.js';
import VectorLayer from "ol/layer/Vector";
import WMTSCapabilities from "ol/format/WMTSCapabilities";
import WMTS, { optionsFromCapabilities } from "ol/source/WMTS";
import TileLayer from "ol/layer/Tile";
import EsriJSON from 'ol/format/EsriJSON.js';
import {
  fromUserExtent,
  fromUserResolution,
  Projection,
  ProjectionLike,
  toUserExtent,
  transformExtent
} from "ol/proj";
import { createStyleFunction } from 'ol-esri-style';
import { Feature } from "ol";
import TileGrid from "ol/tilegrid/TileGrid";
import { Extent } from "ol/extent";

/** Return the numeric part of openlayers style projection */
const projectionToEsriSRID = (projection: Projection) => {
  return projection
    .getCode()
    .split(/:(?=\d+$)/)
    .pop();
};

export class WebSource {
  public id: string;
  protected name: string;
  protected url: string;
  protected enabled = true;
  protected visible = false;
  protected clearable = false;
  protected removeOnClear = true;
  protected layers: Layer[] = [];
  private visibleIdentifier = [];
  protected type: ServerType;
  private handler: WebSourceList

  constructor(webmapHandler: WebSourceList, asset: WebSourceAsset) {
    this.id = asset.id;
    this.name = asset.name;
    this.url = asset.data.url;
    this.handler = webmapHandler;
  }

  get viewer() {
    return this.handler.viewer;
  }

  get map() {
    return this.viewer.minimap.map;
  }

  getByID(identifier) {
    return this.layers.find(layer => {
      return layer.get("identifier") === identifier;
    });
  }

  setLayerVisible(identifier) {
    const layer = this.getByID(identifier);
    if (!layer) return false;

    layer.setVisible(true);
    layer.changed();
    return true;
  }

  toggleVisibility(asset: WebSourceAsset) {
    this.visible = asset.visible;

    const newIdentifiers = asset.data.layers
      .filter(layer => layer.visible && this.visible)
      .map(layer => layer.identifier);

    const needsUpdate = !isEqual(newIdentifiers, this.visibleIdentifier);
    this.visibleIdentifier = newIdentifiers;
    if (!needsUpdate) return;

    // Clear old layers
    this.clearLayers(this.removeOnClear);

    if (newIdentifiers.length === 0) return;
    this.generateLayers(newIdentifiers);
  }

  generateLayers(identifiers: string[]) {
    console.warn("Not Implemented");
  }

  clearLayers(removeLayers) {
    if (removeLayers) {
      // Remove all layers from map
      this.layers.forEach(layer => {
        this.map.removeLayer(layer);
      });
      this.layers = [];
    } else {
      // Set all layer visibility to false
      this.layers.forEach(layer => {
        layer.setVisible(false);
      });
    }
  }

  destroy() {
    this.enabled = false;
    this.clearLayers(true);
  }
}

class FeatureVectorSource extends VectorSource {
  private prevResolution;

  constructor(source: FeatureWebSource, options) {
    const tileGrid = createXYZ({
      tileSize: source.tileSize
    });

    super({
      ...options,
      strategy: (extent, resolution, projection) => {
        return this.getExtents(
          source, tileGrid, extent, resolution, projection
        );
      }
    });
  }

  /** strategy was modified from https://github.com/openlayers/openlayers/blob/v7.5.1/src/ol/loadingstrategy.js */
  getExtents(source: FeatureWebSource, tileGrid: TileGrid, extent: Extent,
    resolution: number, projection: ProjectionLike) {
    if (resolution !== this.prevResolution) {
      source.onResolutionChange();
    }

    this.prevResolution = resolution;

    const z = tileGrid.getZForResolution(
      fromUserResolution(resolution, projection)
    );

    const tileRange = tileGrid.getTileRangeForExtentAndZ(
      fromUserExtent(extent, projection), z
    );

    const extents = [];
    for (let x = tileRange.minX; x <= tileRange.maxX; ++x) {
      for (let y = tileRange.minY; y <= tileRange.maxY; ++y) {
        const tileCoord = [z, x, y];
        const extent = tileGrid.getTileCoordExtent(tileCoord);
        extents.push(toUserExtent(extent, projection));
      }
    }

    return extents;
  }

  updateFeatures(features: Feature[]) {
    let newFeatures = [];

    // Update existing feature geometry
    features.forEach(feature => {
      const featureID = feature.getId();
      const oldFeature = this.getFeatureById(featureID);

      if (oldFeature) {
        const geometry = feature.getGeometry();
        oldFeature.setGeometry(geometry);
      } else {
        newFeatures.push(feature);
      }
    });

    // Add new feature to source
    this.addFeatures(newFeatures);
  }
}

class FeatureWebSource extends WebSource {
  public tileSize = 512;
  protected removeOnClear = false;
  protected controllers: AbortController[] = [];

  onResolutionChange() {
    this.cancelRequests();
    this.clearLoadedExtents();
  }

  cancelRequests() {
    // Cancel any existing requests
    this.controllers.forEach(controller => {
      controller.abort();
    });
  };

  clearLoadedExtents() {
    // Clear all loaded extents
    this.layers.forEach(layer => {
      const source = layer.getSource() as VectorSource;

      // @ts-expect-error
      source.loadedExtentsRtree_.clear();
    });
  }
}

export class ArcGISMapServer extends WebSource {
  protected type = ServerType.ArcGISMapServer;

  /** Generate a single layer for all visible identifiers */
  generateLayers(identifiers: string[]) {
    const layer = new Tile({
      source: new TileArcGISRest({
        crossOrigin: "anonymous",
        url: this.url,
        params: {
          LAYERS: `show: ${identifiers.join(",")}`
        },
        wrapX: false
      })
    });

    this.layers.push(layer);
    this.map.addLayer(layer);
  }
}

export class ArcGISImageServer extends WebSource {
  protected type = ServerType.ArcGISImageServer;

  constructor(webmapHandler: WebSourceList, asset: WebSourceAsset) {
    super(webmapHandler, asset);

    let layer = new Tile({
      source: new TileArcGISRest({
        crossOrigin: "anonymous",
        url: this.url,
        wrapX: false
      }),
      visible: false
    });

    this.layers.push(layer);
    this.map.addLayer(layer);
  }

  toggleVisibility(asset: WebSourceAsset) {
    this.visible = asset.visible;
    this.layers[0].setVisible(this.visible);
  }
}

interface ArcGISFeatureCapabilities {
  metadata: any;
  hasHeight: boolean;
  extent: number[];
  supportsPagination: boolean;
  supportsQuantization: boolean;
  supportsQueryWithResultType: boolean;
}

export class ArcGISFeatureServer extends FeatureWebSource {
  protected type = ServerType.ArcGISFeatureServer;
  private capabilities: { [layer: string]: ArcGISFeatureCapabilities } = {};
  private featureCount: { [layer: string]: number } = {};

  async getCapabilities(identifier: string): Promise<ArcGISFeatureCapabilities> {
    if (identifier in this.capabilities) {
      return this.capabilities[identifier];
    }

    const url = new URL(this.url);
    url.pathname += `/${identifier}`;
    url.searchParams.set('f', 'json');

    const response = await fetch(url.toString());
    const metadata = await response.json();

    const hasHeight = metadata.hasZ;
    const capabilities = metadata.advancedQueryCapabilities || {};
    const {supportsPagination=false, supportsQueryWithResultType=false} = capabilities;
    const supportsQuantization = !!metadata.supportsCoordinatesQuantization;

    let extent;

    try {
      const projection = `EPSG:${metadata.extent.spatialReference.wkid}`;

      extent = transformExtent([
        metadata.extent.xmin, metadata.extent.ymin,
        metadata.extent.xmax, metadata.extent.ymax
      ], projection, 'EPSG:3857');
    } catch (err) {
      console.warn("Error grabbing feature extent");
    }

    this.capabilities[identifier] = {
      metadata,
      hasHeight,
      extent,
      supportsPagination,
      supportsQuantization,
      supportsQueryWithResultType
    };

    return this.capabilities[identifier];
  }

  async getNumberOfFeatures(identifier) {
    if (identifier in this.featureCount) {
      return this.featureCount[identifier];
    }

    const url = new URL(this.url);
    url.pathname += `/${identifier}`;
    url.pathname += '/query';

    url.searchParams.set('f', 'json');
    url.searchParams.set("where", "1=1");
    url.searchParams.set('returnCountOnly', 'true');

    const response = await fetch(url.toString());
    const esriResponse = await response.json();

    this.featureCount[identifier] = esriResponse.count;
    return this.featureCount[identifier];
  }

  /** Generate one layer for each identifier */
  generateLayers(identifiers: string[]) {
    identifiers.forEach(async identifier => {
      // Try to update existing layer visibility
      if (this.setLayerVisible(identifier)) return;

      const capabilities = await this.getCapabilities(identifier);
      const featureCount = await this.getNumberOfFeatures(identifier);

      const vectorSource = new FeatureVectorSource(this, {
        loader: (extent, resolution, projection) => {
          this.getFeatures(vectorSource, identifier,
            extent, projection, capabilities, featureCount);
        },
        wrapX: false
      });

      const layer = new VectorLayer({
        source: vectorSource,
        extent: capabilities.extent,
        properties: {identifier}
      });

      // Apply feature style based on metadata
      const mapProjection = this.map.getView().getProjection();
      const styleFunction = await createStyleFunction(
        capabilities.metadata, mapProjection);
      layer.setStyle(styleFunction);

      if (!this.enabled) return;
      this.layers.push(layer);
      this.map.addLayer(layer);
    });
  }

  reconstructQuantizedGeometry(esriResponse) {
    if (!esriResponse.features) return [];

    const {geometryType} = esriResponse;

    return esriResponse.features.map(feature => {
      const [scaleX, scaleY] = esriResponse.transform.scale;
      const [offsetX, offsetY] = esriResponse.transform.translate;

      const geometry = feature.geometry;

      if (geometryType === "esriGeometryPoint") {
        /** Reconstruct single point */
        geometry.x = offsetX + geometry.x * scaleX;
        geometry.y = offsetY + geometry.y * scaleY;
      } else if (geometryType === "esriGeometryPolygon") {
        /** Reconstruct polygon(s) */
        geometry.rings = geometry.rings.map(ring => {
          let coordinate = [offsetX, offsetY];

          return ring.map(point => {
            coordinate[0] += (point[0]*scaleX);
            coordinate[1] -= (point[1]*scaleY);
            return [...coordinate];
          });
        });
      } else if (geometryType === "esriGeometryPolyline") {
        /** Reconstruct polyline(s) */
        if (geometry.hasOwnProperty("paths")) {
          geometry.paths = geometry.paths.map(path => {
            let coordinate = [offsetX, offsetY];

            return path.map(point => {
              coordinate[0] += (point[0]*scaleX);
              coordinate[1] -= (point[1]*scaleY);
              return [...coordinate];
            });
          });
        }
      } else {
        console.log(`Unknown geometry type: ${geometryType}`);
      }

      return feature;
    });
  }

  async getFeatures(source: FeatureVectorSource, identifier: string, extent,
    projection, capabilities, featureCount, resultOffset=0) {
    const srid = projectionToEsriSRID(projection);

    const {supportsPagination, supportsQuantization,
      supportsQueryWithResultType, hasHeight} = capabilities;

    // Complex layers can use geometry simplification and quantization
    const isComplexLayer = featureCount >= 1000;

    const extendBounds = {
      xmin: extent[0],
      ymin: extent[1],
      xmax: extent[2],
      ymax: extent[3],
      spatialReference: {
        wkid: srid
      }
    };

    // Limit geometry complexity to one vertex point per tile pixel
    const extentWidth = extendBounds.xmax - extendBounds.xmin;
    const tolerance = extentWidth / this.tileSize;

    const quantizationParameters = {
      extent: {
        type: "extent",
        ...extendBounds
      },
      mode: "view",
      originPosition: "upperLeft",
      tolerance
    };

    const url = new URL(this.url);
    url.pathname += `/${identifier}`;
    url.pathname += '/query';

    url.searchParams.set('f', 'json');
    url.searchParams.set('returnGeometry', 'true');
    url.searchParams.set('spatialRel', 'esriSpatialRelIntersects');
    url.searchParams.set('geometry', JSON.stringify(extendBounds));
    url.searchParams.set('geometryType', 'esriGeometryEnvelope');
    url.searchParams.set('inSR', srid);
    url.searchParams.set('outFields', '*');
    url.searchParams.set('outSR', srid);
    url.searchParams.set('where', '1=1');
    url.searchParams.set('returnZ', 'true');

    if (isComplexLayer) {
      if (supportsQuantization) {
        url.searchParams.set('quantizationParameters', JSON.stringify(quantizationParameters));
      } else {
        url.searchParams.set('maxAllowableOffset', tolerance.toString());
      }
    }

    if (supportsPagination) {
      url.searchParams.set('orderByFields', 'OBJECTID');
    }

    if (resultOffset > 0) {
      url.searchParams.set("resultOffset", resultOffset.toString());
    }

    if (supportsQueryWithResultType) {
      // Tile type supports more features per query
      url.searchParams.set('resultType', 'tile');
    }

    // Add controller to array
    const controller = new AbortController();
    this.controllers.push(controller);

    let esriResponse;
    let fetchError = false;

    try {
      const response = await fetch(url.toString(), {
        signal: controller.signal
      });
      esriResponse = await response.json();
    } catch (err) {
      fetchError = true;
    }

    // Remove controller from array
    const index = this.controllers.indexOf(controller);
    this.controllers.splice(index, 1);

    // Something went wrong, exit early
    if (fetchError || esriResponse.error) return;

    if (isComplexLayer) {
      if (supportsQuantization) {
        esriResponse.features = this.reconstructQuantizedGeometry(esriResponse);
      }
    }

    const parser = new EsriJSON();
    const features = parser.readFeatures(esriResponse, {
      featureProjection: projection
    });

    if (features.length === 0) return;
    if (!this.enabled) return;

    // Add new features and update existing ones
    source.updateFeatures(features);

    // TODO: Add feature to scene view
    if (hasHeight) {
      //addSceneFeatures(esriResponse);
    }

    if (esriResponse.exceededTransferLimit && supportsPagination) {
      const newResultOffset = resultOffset + features.length;
      this.getFeatures(source, identifier, extent, projection,
        capabilities, featureCount, newResultOffset);
    }
  }
}

export class OGCWMS extends WebSource {
  protected type = ServerType.OGCWMS;

  /** Generate a single layer for all visible identifiers */
  generateLayers(identifiers: string[]) {
    const layer = new Tile({
      source: new TileWMS({
        crossOrigin: "anonymous",
        url: this.url,
        params: {
          'LAYERS': identifiers.join(","),
          'TILED': true
        },
        serverType: 'geoserver',
        wrapX: false
      })
    });

    if (!this.enabled) return;
    this.layers.push(layer);
    this.map.addLayer(layer);
  }
}

export class OGCWFS extends FeatureWebSource {
  protected type = ServerType.OGCWFS;

  /** Generate one layer for each identifier */
  generateLayers(identifiers: string[]) {
    identifiers.forEach(identifier => {
      // Try to update existing layer visibility
      if (this.setLayerVisible(identifier)) return;

      const vectorSource = new FeatureVectorSource(this, {
        loader: (extent, resolution, projection) => {
          this.getFeatures(vectorSource, identifier,
            extent, projection);
        },
        wrapX: false
      });

      const layer = new VectorLayer({
        source: vectorSource,
        properties: {identifier}
      });

      if (!this.enabled) return;
      this.layers.push(layer);
      this.map.addLayer(layer);
    });
  }

  async getFeatures(source: FeatureVectorSource, identifier: string, extent, projection) {
    const url = new URL(this.url);
    url.searchParams.set('service', 'WFS');
    url.searchParams.set('request', 'GetFeature');
    url.searchParams.set('typename', identifier);
    url.searchParams.set('outputFormat', 'application/json');
    url.searchParams.set('srsname', 'EPSG:3857');

    const geomExtent = `${extent.join(',')},EPSG:3857`;
    url.searchParams.set('bbox', geomExtent);

    // Add controller to array
    const controller = new AbortController();
    this.controllers.push(controller);

    let serverResponse;
    let fetchError = false;

    try {
      const response = await fetch(url.toString(), {
        signal: controller.signal
      });
      serverResponse = await response.json();
    } catch (err) {
      fetchError = true;
    }

    // Remove controller from array
    const index = this.controllers.indexOf(controller);
    this.controllers.splice(index, 1);

    // Something went wrong, exit early
    if (fetchError) return;

    const parser = new GeoJSON();
    const features = parser.readFeatures(serverResponse, {
      featureProjection: projection
    });

    if (features.length === 0) return;
    if (!this.enabled) return;

    // Add new features and update existing ones
    source.updateFeatures(features);
  }
}

export class OGCWMTS extends WebSource {
  protected type = ServerType.OGCWMTS;
  protected removeOnClear = false;
  private capabilities;

  constructor(webmapHandler: WebSourceList, asset: WebSourceAsset) {
    super(webmapHandler, asset);
  }

  async getCapabilities(): Promise<object> {
    if (this.capabilities) {
      return this.capabilities;
    }

    const url = new URL(this.url);
    url.pathname = `${url.pathname}/1.0.0/WMTSCapabilities.xml`;

    const response = await fetch(url.href);
    const text = await response.text();

    const parser = new WMTSCapabilities();
    this.capabilities = parser.read(text);
    return this.capabilities;
  }

  /** Generate one layer for each identifier */
  async generateLayers(identifiers: string[]) {
    let capabilities = await this.getCapabilities();

    identifiers.forEach(identifier => {
      // Try to update existing layer visibility
      if (this.setLayerVisible(identifier)) return;

      const options = optionsFromCapabilities(capabilities, {
        layer: identifier,
        matrixSet: 'EPSG:3857',
        wrapX: false
      });

      const layer = new TileLayer({
        source: new WMTS(options),
        properties: {identifier}
      });

      if (!this.enabled) return;
      this.layers.push(layer);
      this.map.addLayer(layer);
    });
  }
}

export class TileServer extends WebSource {
  protected type = ServerType.TileServer;

  constructor(webmapHandler: WebSourceList, asset: WebSourceAsset) {
    super(webmapHandler, asset);

    let url = this.url
      .replace("{level}", "{z}")
      .replace("{zoom}", "{z}")
      .replace("{col}", "{x}")
      .replace("{row}", "{y}");

    let layer = new Tile({
      source: new XYZ({
        crossOrigin: "anonymous",
        url: url,
        wrapX: false
      }),
      visible: false
    });

    this.layers.push(layer);
    this.map.addLayer(layer);
  }

  toggleVisibility(asset: WebSourceAsset) {
    this.visible = asset.visible;
    this.layers[0].setVisible(this.visible);
  }
}
